/**
 * ELITE AFFIXES
 * -------------
 * Data-driven modifiers rolled onto elite enemies (see docs/DESIGN.md "Elite
 * affixes" and CONTENT_GUIDE.md). An elite gets 1 affix, +1 more with a 25%
 * chance (rolled from the sim rng, so it's deterministic/replayable), no
 * duplicates, weighted by `weight`. Bosses never roll affixes.
 *
 * The bitmask (`AFFIX_BITS`) is what's actually stored per-enemy
 * (`Enemies.affixes: Uint8Array`, src/sim/world.ts) and read by the sim
 * systems (src/sim/systems/enemies.ts, collision.ts) and the renderer
 * (src/render/instanced.ts).
 */

export type AffixId = 'shielded' | 'hasted' | 'splitting' | 'volatile' | 'leeching' | 'warded';

export interface AffixDef {
  id: AffixId;
  name: string;
  desc: string;
  /** Offer weight (relative to the other affixes). */
  weight: number;
}

export const AFFIXES: AffixDef[] = [
  { id: 'shielded', name: 'Shielded', desc: 'Starts with a shield (50% of max HP) that absorbs damage first.', weight: 1 },
  { id: 'hasted', name: 'Hasted', desc: '+40% move speed, fires patterns 40% more often.', weight: 1 },
  { id: 'splitting', name: 'Splitting', desc: 'On death, splits into 3 normal copies of itself.', weight: 1 },
  { id: 'volatile', name: 'Volatile', desc: 'On death, fires a radial burst of slow bullets.', weight: 1 },
  { id: 'leeching', name: 'Leeching', desc: 'Heals for 3x the contact damage it deals to players.', weight: 1 },
  { id: 'warded', name: 'Warded', desc: 'Immune to slow/burn status effects (frost/fire/temporal).', weight: 1 },
];

/** Bitmask assigned to each affix id, stored on `Enemies.affixes[i]`. */
export const AFFIX_BITS: Record<AffixId, number> = {
  shielded: 1 << 0,
  hasted: 1 << 1,
  splitting: 1 << 2,
  volatile: 1 << 3,
  leeching: 1 << 4,
  warded: 1 << 5,
};
